import { privateEmailsManager, xlsImportManager } from '@inpart-io/padme-iap';
import { type ExecutionsManager, type IAsyncExecStatusResult, privateDocumentProperties, privateEmailProperties } from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { SUPPORT_FOLDER } from '@/constants/attachments.constants';
import { DEFAULT_BRIEF_COLOR } from '@/constants/color.options';

import { GET_STATUS_RESULT } from './getters';
import { IMPORT_STATUS_MODULE } from './index';
import { RECEIVE_STATUS_RESULT } from './mutations';
import { type IImportStatusState } from './types';

export const CHECK_PARSING_STATUS = 'checkParsingStatus';
export const PARSE_FILE = 'parseFile';
export const PARSE_FILE_FOR_LEADS = 'parseFileForLeads';
export const IMPORT_DATA = 'importData';
export const IMPORT_LEADS = 'importLeads';
export const PARSE_EMAIL = 'parseEmail';

export const actions: ActionTree<IImportStatusState, unknown> = {
    async [CHECK_PARSING_STATUS](context, { manager, statusId }: { manager: ExecutionsManager<IAsyncExecStatusResult>; statusId?: string }) {
        const result = await manager.checkStatus(statusId);
        if (result?.state === 'RUNNING' || result?.state === 'STARTED') {
            window.setTimeout(() => {
                void context.dispatch(CHECK_PARSING_STATUS, {
                    manager,
                    statusId
                });
            }, 1000);
        }
        context.commit(RECEIVE_STATUS_RESULT, { statusId, statusResult: result });
    },
    async [PARSE_FILE](
        context,
        {
            object,
            file,
            workbookId
        }: {
            object: string;
            file: File;
            workbookId: string;
        }
    ) {
        const statusId = await xlsImportManager.parseFile(object, workbookId, file);
        await context.dispatch(
            `${IMPORT_STATUS_MODULE}/${CHECK_PARSING_STATUS}`,
            {
                manager: xlsImportManager.getExecutionManager(),
                statusId
            },
            { root: true }
        );
        return statusId;
    },
    async [PARSE_FILE_FOR_LEADS](
        context,
        {
            briefId,
            file,
            workbookId
        }: {
            briefId: number;
            file: File;
            workbookId: string;
        }
    ) {
        const statusId = await xlsImportManager.parseFileForLeads(briefId, workbookId, file);
        await context.dispatch(
            `${IMPORT_STATUS_MODULE}/${CHECK_PARSING_STATUS}`,
            {
                manager: xlsImportManager.getExecutionManager(),
                statusId
            },
            { root: true }
        );
        return statusId;
    },
    async [IMPORT_DATA](context, { object, statusId }: { object: string; statusId: string }): Promise<unknown> {
        return xlsImportManager.importData(object, context.rootGetters[`${IMPORT_STATUS_MODULE}/${GET_STATUS_RESULT}`](statusId)?.data);
    },
    async [IMPORT_LEADS](context, { briefId, statusId }: { briefId: number; statusId: string }) {
        return xlsImportManager.importLeads(
            briefId,
            context.rootGetters[`${IMPORT_STATUS_MODULE}/${GET_STATUS_RESULT}`](statusId)?.data,
            DEFAULT_BRIEF_COLOR.value
        );
    },
    async [PARSE_EMAIL](
        context,
        { containerId, containerType, files, folderId }: { containerId: number; containerType: string; files: File[]; folderId?: number }
    ) {
        const createRootFolder = SUPPORT_FOLDER.includes(containerType) && (folderId === undefined || folderId === null);
        const statusId = await privateEmailsManager.parse(files, folderId, createRootFolder, {
            containerId,
            containerType,
            partial: true,
            propertiesDocument: privateDocumentProperties.DEFAULT,
            propertiesEmail: privateEmailProperties.DEFAULT
        });

        await context.dispatch(CHECK_PARSING_STATUS, {
            manager: privateEmailsManager.getExecutionManager(),
            statusId
        });
        return statusId;
    }
};
